import { FC, useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { useSelector } from "react-redux";
import { RootState } from "@store/store";
import { applyMediaSinkId } from "@utils/media-output";

const Wrap = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  display: flex;
`;

const Video = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  visibility: hidden;
  width: 100%;
  max-height: 100%;
`;

const Canvas = styled.canvas`
  width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

export const VideoWrap: FC<{ stream?: MediaStream }> = ({ stream }) => {
  const [playing, setPlaying] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const outputVolume = useSelector(
    (state: RootState) => state.avatar.outputVolume,
  );
  const audioOutputDeviceId = useSelector(
    (state: RootState) => state.avatar.audioOutputDeviceId,
  );

  useEffect(() => {
    const el = videoRef.current;
    if (!stream || !el) {
      return;
    }

    let timeCheckInterval: ReturnType<typeof setInterval> | undefined;
    let cancelled = false;

    el.srcObject = stream;
    el.onloadedmetadata = () => {
      if (cancelled) {
        return;
      }
      const playPromise = el.play();
      if (playPromise !== undefined) {
        void playPromise.catch(() => {
          // pause() / srcObject cleared before play() settled — safe to ignore
        });
      }

      timeCheckInterval = setInterval(() => {
        const v = videoRef.current;
        if (cancelled || !v || v.currentTime <= 0) {
          return;
        }
        setPlaying(true);
        if (timeCheckInterval !== undefined) {
          clearInterval(timeCheckInterval);
          timeCheckInterval = undefined;
        }
      }, 5);
    };

    return () => {
      cancelled = true;
      if (timeCheckInterval !== undefined) {
        clearInterval(timeCheckInterval);
      }
      el.onloadedmetadata = null;
      el.pause();
      el.srcObject = null;
      setPlaying(false);
    };
  }, [stream]);

  useEffect(() => {
    const el = videoRef.current;
    if (el) {
      el.volume = outputVolume ?? 1;
    }
  }, [outputVolume, stream]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) {
      return;
    }
    const sinkId = (audioOutputDeviceId ?? "") || "";
    void applyMediaSinkId(el, sinkId).catch((e) => console.warn(e));
  }, [audioOutputDeviceId, stream]);

  useEffect(() => {
    if (!playing || !videoRef.current || !canvasRef.current) {
      return;
    }

    const renderCanvas = () => {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!canvas || !video) {
        return;
      }
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) {
        return;
      }

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      if (!canvas.width) {
        return;
      }

      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      for (let i = 0; i < data.length; i += 4) {
        const red = data[i];
        const green = data[i + 1];
        const blue = data[i + 2];

        if (isCloseToGreen([red, green, blue])) {
          data[i + 3] = 0; // Set alpha channel to 0 (transparent)
        }
      }

      ctx.putImageData(imageData, 0, 0);

      return requestAnimationFrame(renderCanvas); // Return the request ID
    };

    const isCloseToGreen = (color: number[]) => {
      const [red, green, blue] = color;
      const th = 90; // Adjust the threshold values for green detection
      return green > th && red < th && blue < th;
    };

    const animationFrameId = renderCanvas(); // Start the animation loop

    // Clean up function to cancel animation frame
    return () => cancelAnimationFrame(animationFrameId!);
  }, [playing, videoRef, canvasRef]);

  return (
    <Wrap>
      <Video playsInline ref={videoRef} />
      <Canvas ref={canvasRef}></Canvas>
    </Wrap>
  );
};

export default VideoWrap;
